import React, { useEffect, useState } from "react";
import styles from "./assets/scss/login.module.scss";
import CustomTextField from "components/mui/text-field/text-field";
import CustomPasswordField from "components/mui/pass-text-field/pass-text-field";
import { Button } from "components/mui/button";
import axios from "axios";
import EmailIcon from "assets/imgs/email.svg";
import passicon from "assets/imgs/password.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { uiActions } from "store/ui/ui-slice";
import { appActions } from "store/slices/app";
import { useTheme } from "@mui/material";
import { Boarding } from "layouts/boarding";
import { setAuthToken, clearAuthToken, getAuthToken } from "utils/auth";
import { MemberStatus } from "./enums/user";
import { UserRole, UserType } from "enums/account";
import { postRequest, getRequest } from "utils/request";
import { MediaFolders } from "enums/request";

const Login = () => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);

    const passwordValidation = validatePassword(passwordValue);
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      password: passwordValidation,
    }));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);

    if (!event.target.value) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email is required.",
      }));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(event.target.value)) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        email: "Enter a valid email.",
      }));
    } else {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        email: "",
      }));
    }
  };

  const apiURL = process.env.REACT_APP_API_URL || "";

  const validateInputs = () => {
    const errors: { email: string; password: string } = {
      email: validationErrors.email,
      password: validationErrors.password,
    };

    return !errors.email && !errors.password;
  };
  const fetchEntityAvatar = async (avatarFilename: string) => {
    if (!avatarFilename) return null;

    try {
      const response = await getRequest(
        `get_file/`,
        {
          folder: MediaFolders.ENTITY_IMAGES,
          filename: avatarFilename,
          as_attachment: false,
        },
        navigate,
        {},
        "blob",
      );
      if (response?.data) {
        const imageUrl = URL.createObjectURL(new Blob([response.data]));
        return imageUrl;
      }
    } catch (error) {
      console.error("Error fetching entity image:", error);
    }
    return null;
  };
  const fetchAllEntityAvatars = async (entitiesInfo: any[]) => {
    if (!entitiesInfo || !Array.isArray(entitiesInfo)) return entitiesInfo;

    const updatedEntities = await Promise.all(
      entitiesInfo.map(async (entity: any) => {
        if (entity.entity_avatar || entity.avatar) {
          const avatarFilename = entity.entity_avatar || entity.avatar;
          const avatarUrl = await fetchEntityAvatar(avatarFilename);
          return {
            ...entity,
            entity_avatar: avatarFilename,
            avatarUrl: avatarUrl, // Store the actual image URL
          };
        }
        return entity;
      }),
    );

    return updatedEntities;
  };
  const handleLogin = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const targetPage = urlParams.get("target");
    if (!validateInputs()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${apiURL}/login/`, {
        email: inputValue,
        password: password,
      });

      console.log("Login Response:", response);

      const data = response.data.data;

      if (data) {
        console.log("Login data received:", data);

        // Fetch entity avatars before setting app data
        if (data.entities_info && Array.isArray(data.entities_info)) {
          const entitiesWithAvatars = await fetchAllEntityAvatars(
            data.entities_info,
          );

          const updatedData = {
            ...data,
            entities_info: entitiesWithAvatars,
          };
          dispatch(
            appActions.setAppData({
              ...response.data,
              data: updatedData,
            }),
          );
        } else {
          dispatch(appActions.setAppData(response.data));
        }

        if ("token" in data) {
          setAuthToken(data.token);
        }

        if ("is_temp" in data) {
          const temp = data?.is_temp;
          const selectedEntity = data.entities_info?.find(
            (entity: any) => entity.role === UserRole.ROOT,
          );
          if (!selectedEntity) {
            dispatch(
              uiActions.updateNotification({
                message: "You're not allowed to log in.",
                level: "error",
              }),
            );
            return;
          }
          const status = selectedEntity?.status;

          if (status === MemberStatus.ACTIVE && temp === false) {
            navigate(`../${targetPage ? targetPage : "entities"}`);
          } else {
            dispatch(
              uiActions.updateNotification({
                message: "Something went wrong.",
                level: "error",
              }),
            );
          }
        }
      }

      if (response.data.isOk) {
        dispatch(
          uiActions.updateNotification({
            message: response.data.message,
            level: "success",
          }),
        );
      } else {
        dispatch(
          uiActions.updateNotification({
            message: response.data.message,
            level: "error",
          }),
        );
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Login failed. Please try again.";
      console.error("Login Failed:", message);

      dispatch(
        uiActions.updateNotification({
          message,
          level: "error",
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled = isSubmitting;

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number.";
    }
    return "";
  };
  useEffect(() => {
    if (getAuthToken()) {
      navigate("/entities");
    }
  }, []);
  return (
    <Boarding>
      <div
        className={styles.upperPart}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleLogin();
          }
        }}
        tabIndex={0}
      >
        <div className={styles.upperPart}>
          <div className={styles.inputContainer}>
            <div className={styles.signupElement}>
              <label
                style={{
                  color: theme.custom.font.black,
                }}
                className={styles["label"]}
              >
                E-mail
              </label>
              <CustomTextField
                placeholder="default@example.com"
                width="100%"
                value={inputValue}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                variant="outlined"
                iconSrc={EmailIcon}
              />
            </div>
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.signupElement}>
              <label
                style={{
                  color: theme.custom.font.black,
                  fontWeight: 400,
                }}
                className={styles["label"]}
              >
                Password
              </label>
              <CustomPasswordField
                iconSrc={passicon}
                placeholder="At least 8 characters"
                value={password}
                width="100%"
                onChange={handlePasswordChange}
                error={!!validationErrors.password}
                helperText={validationErrors.password}
              />
            </div>
          </div>
          <Button
            label="Login"
            sx={{
              color: theme.custom.neutralColors.color11,
              backgroundColor: theme.palette.primary.light,
              width: "100%",
            }}
            disabled={isButtonDisabled}
            onClick={handleLogin}
            className={styles["loginBtn"]}
          />
        </div>
        <hr
          color={theme.palette.secondary.light}
          style={{ border: "0", borderTop: "1px solid" }}
        />
        <div className={styles.lowerPart}>
          <p>
            By proceeding, you agree to the{" "}
            <span style={{ color: theme.palette.primary.light }}>
              Terms of Service{" "}
            </span>
            and{" "}
            <span style={{ color: theme.palette.primary.light }}>
              Privacy Policy
            </span>
          </p>
        </div>
      </div>
    </Boarding>
  );
};

export default Login;
